import useCreateOrder, {
  CreateOrderProductProps,
} from 'modules/orders/hooks/use-create-order';
import {
  OrderOriginEnum,
  OrderProductProps,
  OrderTypeEnum,
} from 'modules/orders/types';
import React, {useCallback, useEffect, useState} from 'react';
import Column from 'shared/components/column';
import {SemiBoldText} from 'shared/components/text';
import roundTo from 'shared/util/round-to';
import CartDocument from '../components/cart-document';
import CartHeader from '../components/cart-header';
import CartObservation from '../components/cart-observation';
import CartPayment from '../components/cart-payment';
import CartProducts from '../components/cart-products';
import CartSummary from '../components/cart-summary';
import {CartAdditionalGroupProps, CartProductProps} from '../types';
import useCart from '../util/use-cart';
import Button from 'shared/migration-components/button';
import CartAddress from '../components/cart-address';
import styled from 'styled-components';
import CartUser from '../components/cart-user';
import {useNavigate, useParams} from 'react-router-dom';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import useOrder from 'modules/orders/hooks/use-order';
import useApi from 'shared/hooks/use-api';
import useUpdateOrder from 'modules/orders/hooks/use-update-order';
import Loading from 'shared/migration-components/loading';

const Cart: React.FC = () => {
  const {
    cart,
    deliveryPrice,
    total,
    deliveryPriceQuery,
    freeDeliveryThreshold,
    userByPhoneQuery,
    setCart,
    companyQuery,
    userQuery,
  } = useCart();

  const {id} = useParams();
  const createOrderMutation = useCreateOrder();
  const updateOrderMutation = useUpdateOrder(id!);
  const navigate = useNavigate();
  const {companyId} = useAccessControls();
  const api = useApi();
  const [isLoading, setIsLoading] = useState(!!id);

  const orderDetailsQuery = useOrder(id);

  const handleStartCart = useCallback(async () => {
    if (!orderDetailsQuery.data) return;

    const {
      companyId,
      userId,
      guestUser,
      type,
      address,
      payment,
      coupon,
      balanceTotal,
      ...order
    } = orderDetailsQuery.data;

    const {data} = await api.get<{data: OrderProductProps[]}>(
      `/products/ids?productsIds=${JSON.stringify(
        order.products.map((product) => product.id),
      )}`,
    );

    const productsByIds = data.data;

    const products = order.products.map(
      (product): CartProductProps => ({
        companyId: !!product.companyId,
        id: product.id,
        quantity: product.quantity,
        observation: product.observation,
        onePerOrder: product.onePerOrder,
        saleId: product.saleId,
        additionalGroups: product.additionalGroups?.reduce(
          (additionalGroups, {id, additionals}) => ({
            ...additionalGroups,
            [id]: additionals.map((additional) => ({
              id: additional.id,
              quantity: additional.quantity,
            })),
          }),
          {} as CartAdditionalGroupProps,
        ),
        size: product.size
          ? productsByIds
              .find((someProduct) => someProduct.id === product.id)
              ?.sizes.find((someSize) => someSize.name === product.size)
          : undefined,
      }),
    );

    setCart({
      companyId,
      payment,
      type,
      products,
      userId,
      guestUser,
      address,
      coupon,
      balance: balanceTotal,
    });

    setIsLoading(false);
  }, [orderDetailsQuery.data]);

  useEffect(() => {
    handleStartCart();
  }, [handleStartCart]);

  const formatProducts = (
    products: CartProductProps[],
  ): CreateOrderProductProps[] =>
    products.map((product) => {
      const additionalGroups = Object.keys(product.additionalGroups ?? {}).map(
        (id) => ({
          id,
          additionals: product.additionalGroups![id],
        }),
      );

      return {
        ...product,
        size: product.size?.name,
        additionalGroups,
      };
    });

  const handleCreateOrder = () => {
    if (!cart || !cart.type || (!cart?.guestUser && !userByPhoneQuery.data))
      return;

    const payment = cart.payment!;

    if (id)
      return updateOrderMutation.mutate({
        payment,
        products: formatProducts(cart.products),
        type: cart.type,
        address:
          cart.type === OrderTypeEnum.delivery ? cart.address : undefined,
        total: roundTo(
          total +
            (deliveryPriceQuery.data?.freeDelivery || freeDeliveryThreshold
              ? 0
              : deliveryPrice),
        ),
        document: cart?.document,
        deliveryPrice,
        deliveryManFee: deliveryPriceQuery.data?.deliveryManFee,
        freeDelivery:
          deliveryPriceQuery.data?.freeDelivery === true
            ? !!deliveryPriceQuery.data?.freeDelivery
            : !!freeDeliveryThreshold,
        observation: cart?.observation,
        retainedEarnings: cart?.retainedEarnings,
      });

    createOrderMutation.mutate({
      type: cart.type,
      address: cart.type === OrderTypeEnum.delivery ? cart.address : undefined,
      products: formatProducts(cart.products),
      payment,
      companyId: companyId!,
      deliveryPrice,
      total: roundTo(
        total +
          (deliveryPriceQuery.data?.freeDelivery || freeDeliveryThreshold
            ? 0
            : deliveryPrice),
      ),
      document: cart?.document,
      deliveryManFee: deliveryPriceQuery.data?.deliveryManFee,
      polyline: deliveryPriceQuery.data?.polyline,
      freeDelivery:
        deliveryPriceQuery.data?.freeDelivery === true
          ? !!deliveryPriceQuery.data?.freeDelivery
          : !!freeDeliveryThreshold,
      guestUser:
        !userByPhoneQuery.data?.id && cart?.guestUser
          ? cart.guestUser
          : undefined,
      userId: userByPhoneQuery.data?.id,
      origin: OrderOriginEnum.system,
      observation: cart?.observation,
      retainedEarnings: cart?.retainedEarnings,
    });
  };

  const desktopConfirmation = () => {
    if (!cart?.payment) return navigate(`${companyId}/payment-methods`);
    if (cart?.type === OrderTypeEnum.delivery && !cart.address)
      return navigate('address');
    if (!cart?.products || cart.products.length === 0)
      return navigate(`${companyId}/products`);

    return handleCreateOrder();
  };

  const desktopConfirmationLabel = () => {
    if (!cart?.payment) return 'Escolher pagamento';
    if (cart?.type === OrderTypeEnum.delivery && !cart.address)
      return 'Escolher endereço';
    if (!cart?.products || cart.products.length === 0)
      return 'Selecionar produtos';
    return id ? 'Alterar pedido' : 'Enviar pedido';
  };

  if (id && (isLoading || companyQuery.isLoading || userQuery.isLoading))
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <Loading size={24} />
      </div>
    );

  return (
    <Container>
      <Content>
        <Column style={{width: 600}} gap={20}>
          <CartUser />
          <Column gap={15}>
            <SemiBoldText size="large">Endereço</SemiBoldText>
            <CartAddress />
          </Column>
          <Column gap={15}>
            <SemiBoldText size="large">Pagamento</SemiBoldText>
            <CartPayment />
            <CartDocument />
          </Column>
          <CartObservation />
          <Button
            onClick={desktopConfirmation}
            title={desktopConfirmationLabel()}
            loading={
              createOrderMutation.isLoading || updateOrderMutation.isLoading
            }
            style={{flex: 1, minHeight: 40, maxHeight: 40}}
          />
        </Column>
        <div
          style={{
            width: 450,
            padding: 20,
            borderRadius: 8,
            boxShadow: '0px 2px 5.5px rgba(0, 0, 0, 0.32)',
            overflow: 'hidden',
            gap: 15,
            position: 'sticky',
            top: 100,
          }}>
          <CartHeader />
          <CartProducts />
          <CartSummary />
        </div>
      </Content>
    </Container>
  );
};

export default Cart;

export const Container = styled.div`
  display: flex;
  flex: 1;
  background-color: white;
  padding: 50px 0;
`;

export const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 100px;
`;
